import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Image, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { ChildProfile, getProfiles, saveSelectedProfile } from '../api/profiles';
import { avatarSource } from '../utils/avatars';

type Props = NativeStackScreenProps<RootStackParamList, 'ProfileSelect'>;

export default function ProfileSelectScreen({ navigation }: Props) {
  const [profiles, setProfiles] = useState<ChildProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);
  const [answer, setAnswer] = useState('');

  const gate = useMemo(() => {
    const left = Math.floor(Math.random() * 5) + 2;
    const right = Math.floor(Math.random() * 5) + 2;
    return { left, right, result: left + right };
  }, [gateOpen]);

  const loadProfiles = useCallback(() => {
    setLoading(true);
    getProfiles()
      .then((items) => {
        setProfiles(items);
        if (!items.length) {
          navigation.replace('CreateChildProfile');
        }
      })
      .catch((error) => {
        Alert.alert(
          'Không tải được hồ sơ',
          error instanceof Error ? error.message : 'Vui lòng kiểm tra kết nối và thử lại.',
        );
      })
      .finally(() => setLoading(false));
  }, [navigation]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', loadProfiles);
    loadProfiles();
    return unsubscribe;
  }, [loadProfiles, navigation]);

  const selectProfile = async (profile: ChildProfile) => {
    await saveSelectedProfile(profile);
    navigation.reset({ index: 0, routes: [{ name: 'Home' }] });
  };

  const submitGate = () => {
    if (Number(answer) !== gate.result) {
      Alert.alert('Chưa đúng rồi', 'Hãy nhờ ba mẹ thử lại phép tính nhé.');
      return;
    }

    setAnswer('');
    setGateOpen(false);
    navigation.navigate('CreateChildProfile');
  };

  return (
    <View style={styles.screen}>
      <GradientBackground fullHeight />
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>Ai đang nghe?</Text>
        <Text style={styles.subtitle}>Chọn hồ sơ để mở thư viện phù hợp với bé.</Text>

        <View style={styles.profileGrid}>
          {loading ? (
            <Text style={styles.emptyText}>Đang tải hồ sơ...</Text>
          ) : profiles.map((profile) => (
            <Pressable key={profile.id} style={styles.profileItem} onPress={() => selectProfile(profile)}>
              <View style={styles.profileAvatar}>
                <Image source={avatarSource(profile.avatar_url)} style={styles.profileImage} resizeMode="contain" />
              </View>
              <Text style={styles.profileName} numberOfLines={2}>{profile.nickname}</Text>
            </Pressable>
          ))}
        </View>
      </ScrollView>

      <Pressable style={styles.addButton} onPress={() => setGateOpen(true)}>
        <Ionicons name="add" size={18} color={colors.buttonText} />
        <Text style={styles.addButtonText}>Thêm hồ sơ</Text>
      </Pressable>

      <Modal transparent visible={gateOpen} animationType="fade" onRequestClose={() => setGateOpen(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Câu hỏi cho phụ huynh</Text>
            <Text style={styles.modalText}>{gate.left} + {gate.right} = ?</Text>
            <TextInput
              value={answer}
              onChangeText={setAnswer}
              keyboardType="number-pad"
              style={styles.answerInput}
              placeholder="Nhập đáp án"
              placeholderTextColor={colors.textSecondary}
            />
            <View style={styles.modalActions}>
              <Pressable style={styles.cancelButton} onPress={() => setGateOpen(false)}>
                <Text style={styles.cancelText}>Hủy</Text>
              </Pressable>
              <Pressable style={styles.confirmButton} onPress={submitGate}>
                <Text style={styles.confirmText}>Tiếp tục</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 78,
    paddingBottom: 116,
  },
  title: {
    color: colors.buttonText,
    fontSize: 30,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 34,
  },
  profileGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 22,
  },
  emptyText: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '800',
  },
  profileItem: {
    width: 128,
    alignItems: 'center',
  },
  profileAvatar: {
    width: 112,
    height: 112,
    borderRadius: 56,
    backgroundColor: colors.tertiary,
    borderWidth: 5,
    borderColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  profileImage: {
    width: 82,
    height: 82,
  },
  profileName: {
    color: colors.buttonText,
    fontSize: 16,
    fontWeight: '900',
    textAlign: 'center',
    lineHeight: 20,
  },
  addButton: {
    position: 'absolute',
    left: 24,
    right: 24,
    bottom: 28,
    height: 48,
    borderRadius: 18,
    backgroundColor: colors.button,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  addButtonText: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '900',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(18, 51, 122, 0.38)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: colors.panel,
    borderRadius: 24,
    padding: 20,
  },
  modalTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '900',
    marginBottom: 10,
  },
  modalText: {
    color: colors.textPrimary,
    fontSize: 26,
    fontWeight: '900',
    marginBottom: 14,
  },
  answerInput: {
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
    color: colors.textPrimary,
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    height: 42,
    paddingHorizontal: 16,
    borderRadius: 14,
    backgroundColor: colors.card,
    justifyContent: 'center',
  },
  cancelText: {
    color: colors.textPrimary,
    fontWeight: '900',
  },
  confirmButton: {
    height: 42,
    paddingHorizontal: 18,
    borderRadius: 14,
    backgroundColor: colors.button,
    justifyContent: 'center',
  },
  confirmText: {
    color: colors.buttonText,
    fontWeight: '900',
  },
});
